import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Video, Lock, Calendar, MapPin, Shield, ArrowUpRight } from "lucide-react";

const features = [
  {
    icon: Video,
    title: "Face-to-face on screen",
    body: "High-quality video sessions designed for clinical interaction — not just a meeting room.",
  },
  {
    icon: Lock,
    title: "Private & compliant",
    body: "Secure, encrypted platforms aligned with HIPAA (USA) and GDPR (EU/Ireland) requirements.",
  },
  {
    icon: Calendar,
    title: "Flexible scheduling",
    body: "Sessions across U.S. and Irish time zones, with evening availability where possible.",
  },
];

export const Telehealth = ({ onBookClick }) => {
  return (
    <section
      id="telehealth"
      data-testid="telehealth-section"
      className="relative py-20 md:py-32 bg-[#2D332F] text-[#FDFCF8] overflow-hidden"
    >
      <div
        className="orb orb-sage"
        style={{ width: 480, height: 480, top: -100, left: -180, opacity: 0.35 }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4B996] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D4B996]" /> Telehealth, done well
            </p>
            <h2
              data-testid="telehealth-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight"
            >
              Therapy that travels with you,
              <br />
              <em className="text-[#D4B996]">across continents.</em>
            </h2>
            <p className="mt-6 text-[#FDFCF8]/75 text-lg leading-relaxed">
              Whether you are based in Dublin, Boston, Galway or Brooklyn —
              consistent, high-fidelity therapy is one secure link away.
            </p>

            <div className="mt-8 rounded-2xl border border-[#D4B996]/30 bg-[#D4B996]/10 p-5">
              <div className="flex items-center gap-2 text-[#D4B996] text-xs uppercase tracking-[0.22em] mb-2">
                <Shield size={14} strokeWidth={1.5} /> HIPAA &amp; GDPR aligned
              </div>
              <p className="text-sm text-[#FDFCF8]/85 leading-relaxed">
                Sessions delivered via{" "}
                <strong className="text-[#FDFCF8]">Zoom for Healthcare</strong>{" "}
                under an active Business Associate Agreement. Consent is
                captured before every session. Read our{" "}
                <Link
                  to="/privacy"
                  data-testid="telehealth-privacy-link"
                  className="underline text-[#D4B996] hover:text-white"
                >
                  Privacy Notice
                </Link>
                .
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={onBookClick}
                  data-testid="telehealth-book-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FDFCF8] text-[#2D332F] text-sm hover:bg-[#D4B996] transition"
                >
                  Request appointment
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </button>
                <Link
                  to="/join"
                  data-testid="telehealth-join-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/30 text-[#FDFCF8] text-sm hover:bg-white/10 transition"
                >
                  Join your session
                </Link>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="rounded-2xl border border-white/10 p-5 bg-white/5 backdrop-blur-sm">
                <MapPin
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#D4B996] mb-3"
                />
                <p className="text-xs uppercase tracking-[0.2em] text-[#FDFCF8]/60 mb-1">
                  Licensed in
                </p>
                <p className="font-serif text-2xl">New Hampshire &amp; Vermont</p>
                <p className="text-sm text-[#FDFCF8]/70 mt-1">ASHA CCC-SLP</p>
              </div>
              <div className="rounded-2xl border border-white/10 p-5 bg-white/5 backdrop-blur-sm">
                <MapPin
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#D4B996] mb-3"
                />
                <p className="text-xs uppercase tracking-[0.2em] text-[#FDFCF8]/60 mb-1">
                  Licensed in
                </p>
                <p className="font-serif text-2xl">Ireland</p>
                <p className="text-sm text-[#FDFCF8]/70 mt-1">CORU Registered</p>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[16/10] border border-white/10">
              <img
                src="https://images.pexels.com/photos/3205403/pexels-photo-3205403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Telehealth session setup"
                loading="lazy"
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#2D332F] via-[#2D332F]/30 to-transparent" />
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    data-testid={`telehealth-feature-${f.title.toLowerCase().replace(/\s|&/g, "-")}`}
                    className="rounded-2xl border border-white/10 p-5 bg-white/5 backdrop-blur-sm"
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.5}
                      className="text-[#D4B996] mb-3"
                    />
                    <p className="font-medium text-[#FDFCF8] mb-1.5">
                      {f.title}
                    </p>
                    <p className="text-sm text-[#FDFCF8]/70 leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
